import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Activity, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import useClusterApiKey from '../../hooks/useClusterApiKey';

import { parseUtc } from '../../lib/parseUtc';
/**
 * W7 — Spot Reliability panel (Node Optimization page).
 *
 * Surfaces the spot-risk telemetry the in-cluster engine already consumes:
 *   GET /v1/clusters/{id}/spot-risk                       — forecaster mode +
 *       per-node interruption probabilities (X-API-Key auth, same agent
 *       key resolved via useClusterApiKey)
 *   GET /v1/clusters/{id}/preemption-events/summary       — recent
 *       interruption / rebalance / stockout counts (JWT dashboard route)
 *
 * Degrades gracefully: no spot nodes is a valid empty state, and a missing
 * cluster API key only hides the node-risk table (counts still render).
 */

const REFRESH_MS = 60_000;
const SUMMARY_HOURS = 24;

const MODE_BADGE = {
  reactive:   'bg-slate-100 text-slate-700',
  shadow:     'bg-sky-100 text-sky-800',
  predictive: 'bg-indigo-100 text-indigo-800',
};

const riskClass = (p) => {
  if (p >= 0.5) return 'text-red-600 font-semibold';
  if (p >= 0.2) return 'text-amber-600 font-medium';
  return 'text-emerald-700';
};

const pct = (v, digits = 0) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`;

// Calibration chip tint by |predicted − observed| gap.
const calibrationTint = (diff) => {
  if (diff <= 0.1) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (diff <= 0.25) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
};

// Rows to highlight in the threshold table: 0.4 is the derank threshold,
// 0.70 is the disruptor act threshold.
const isKeyThreshold = (t) => t === 0.4 || t === 0.7;

const CountTile = ({ label, value, alert }) => (
  <div className="rounded-md border border-slate-200 p-3">
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    <div
      className={`text-2xl font-semibold mt-1 tabular-nums ${
        alert && value > 0 ? 'text-amber-600' : 'text-slate-900 dark:text-slate-100'
      }`}
    >
      {value ?? '—'}
    </div>
    <div className="text-xs text-slate-400 mt-0.5">last {SUMMARY_HOURS}h</div>
  </div>
);

// The three forecaster modes, in escalation order. "predictive" is the acting
// mode ("on"): label at-risk nodes for pre-emptive live-migration rescue before
// any AWS signal. Until 2026-07-27 there was NO write path for this setting —
// the badge displayed a value nothing in the product could change.
const MODES = [
  { value: 'reactive', label: 'Off (reactive)', hint: 'Act on real AWS signals only (interruption notice + rebalance recommendation).' },
  { value: 'shadow', label: 'Shadow', hint: 'Also score forecasts and log would-have-acted. Never acts early.' },
  { value: 'predictive', label: 'On (predictive)', hint: 'Act on forecasts BEFORE AWS signals: at-risk nodes are pre-emptively rescued via live migration. Uses the promoted model when available, else the heuristic risk blend.' },
];

const SpotReliabilityPanel = ({ clusterId }) => {
  const { apiKey, loading: keyLoading } = useClusterApiKey(clusterId);
  const [risk, setRisk] = useState(null);
  const [summary, setSummary] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [shadowError, setShadowError] = useState(null);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [modeOverride, setModeOverride] = useState(null);

  const saveMode = async (value) => {
    setModeSaving(true);
    try {
      const res = await axios.post(
        `${API}/v1/clusters/${clusterId}/node-optimization/spot-forecaster-mode`,
        { mode: value },
      );
      setModeOverride(res.data?.spot_forecaster_mode || value);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setModeSaving(false);
    }
  };

  // JWT summary counts — independent of the agent key.
  useEffect(() => {
    if (!clusterId) return undefined;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const res = await axios.get(
          `${API}/v1/clusters/${clusterId}/preemption-events/summary?hours=${SUMMARY_HOURS}`,
        );
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      }
    };
    fetchSummary();
    const id = setInterval(fetchSummary, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clusterId]);

  // Spot-risk feed — needs the cluster-scoped agent key (X-API-Key auth).
  useEffect(() => {
    if (!clusterId || !apiKey) return undefined;
    let cancelled = false;
    const fetchRisk = async () => {
      try {
        const res = await axios.get(`${API}/v1/clusters/${clusterId}/spot-risk`, {
          headers: { 'X-API-Key': apiKey },
        });
        if (!cancelled) {
          setRisk(res.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      }
    };
    fetchRisk();
    const id = setInterval(fetchRisk, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clusterId, apiKey]);

  // Shadow accuracy — resolved Stage-A forecasts scored against real
  // interruptions (same cluster-scoped X-API-Key as the risk feed).
  useEffect(() => {
    if (!clusterId || !apiKey) return undefined;
    let cancelled = false;
    const fetchShadow = async () => {
      try {
        const res = await axios.get(
          `${API}/v1/clusters/${clusterId}/spot-risk/shadow-metrics`,
          { headers: { 'X-API-Key': apiKey } },
        );
        if (!cancelled) {
          setShadow(res.data);
          setShadowError(null);
        }
      } catch (err) {
        if (!cancelled) setShadowError(err.response?.data?.detail || err.message);
      }
    };
    fetchShadow();
    const id = setInterval(fetchShadow, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clusterId, apiKey]);

  const nodes = risk?.nodes || [];
  // modeOverride wins right after a save; the 60s risk poll converges to it.
  const mode = modeOverride || risk?.mode;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-600" />
          Spot Reliability
          {mode && (
            <Badge
              className={`text-xs ${MODE_BADGE[mode] || 'bg-gray-100 text-gray-700'}`}
              title="Forecaster mode (node_optimization_config.spot_forecaster_mode)"
            >
              {mode}
            </Badge>
          )}
          {summary && summary.interruptions > 0 && (
            <Badge className="bg-amber-100 text-amber-800 text-xs inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {summary.interruptions} interruption{summary.interruptions > 1 ? 's' : ''} / {SUMMARY_HOURS}h
            </Badge>
          )}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </Button>
      </CardHeader>

      {!collapsed && (
        <CardContent className="space-y-3">
          {error && (
            <p className="text-xs text-red-600">Spot reliability API error: {error}</p>
          )}

          {/* Forecaster mode selector — reactive / shadow / predictive ("on") */}
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">
              Predictive spot rescue
            </div>
            <div className="flex flex-wrap gap-2">
              {MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  disabled={modeSaving}
                  onClick={() => saveMode(m.value)}
                  title={m.hint}
                  className={`text-xs px-3 py-1 rounded border transition-colors ${
                    mode === m.value
                      ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
                  } ${modeSaving ? 'opacity-60' : ''}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {(MODES.find((m) => m.value === mode) || MODES[0]).hint} Reactive rescue
              (real interruption notices) is always on — this controls acting on
              forecasts ahead of them.
            </p>
          </div>

          {/* Recent event counts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <CountTile label="Interruptions" value={summary?.interruptions} alert />
            <CountTile label="Rebalance recs" value={summary?.rebalance_recommendations} />
            <CountTile label="Stockouts" value={summary?.stockouts} alert />
          </div>

          {/* Per-node interruption probabilities */}
          {!keyLoading && !apiKey ? (
            <p className="text-xs text-gray-400">
              No active API key found for this cluster — the per-node risk feed is unavailable.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 border-b">
                  <tr>
                    <th className="py-1.5 pr-3 font-medium">Node</th>
                    <th className="py-1.5 pr-3 font-medium">Instance Type</th>
                    <th className="py-1.5 pr-3 font-medium">Zone</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Interruption Risk</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Confidence</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Horizon</th>
                  </tr>
                </thead>
                <tbody>
                  {nodes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-gray-400 text-xs">
                        {risk
                          ? 'No spot nodes in the latest snapshot — nothing at risk.'
                          : 'Loading spot-risk feed…'}
                      </td>
                    </tr>
                  )}
                  {nodes.map((n) => (
                    <tr key={n.node_name || `${n.instance_type}-${n.zone}`} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <code className="text-xs bg-gray-50 px-1 rounded">{n.node_name || '—'}</code>
                      </td>
                      <td className="py-2 pr-3 text-xs">{n.instance_type || '—'}</td>
                      <td className="py-2 pr-3 text-xs text-gray-600">{n.zone || '—'}</td>
                      <td className={`py-2 pr-3 text-right text-xs tabular-nums ${riskClass(n.probability || 0)}`}>
                        {Math.round((n.probability || 0) * 100)}%
                      </td>
                      <td className="py-2 pr-3 text-right text-xs tabular-nums text-gray-600">
                        {Math.round((n.confidence || 0) * 100)}%
                      </td>
                      <td className="py-2 pr-3 text-right text-xs text-gray-600">
                        {n.horizon_minutes ? `${n.horizon_minutes}m` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-xs text-gray-500">
            Same forecast feed the in-cluster engine polls — probabilities are
            per (family, zone) over the stated horizon.
            {risk?.scored_at && ` Scored ${parseUtc(risk.scored_at).toLocaleTimeString()}.`}
          </div>

          {/* Prediction accuracy (shadow) — scored forecasts vs real interruptions */}
          <div className="rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap items-baseline gap-2 mb-2">
              <div className="text-xs text-slate-500 uppercase tracking-wide">
                Prediction accuracy (shadow)
              </div>
              {shadow && !shadow.note && (
                <span className="text-xs text-gray-500">
                  last {shadow.days}d · {shadow.resolved} predictions resolved ·
                  base rate {pct(shadow.base_rate, 1)}
                </span>
              )}
            </div>

            {shadowError ? (
              <p className="text-xs text-gray-400">Shadow metrics unavailable: {shadowError}</p>
            ) : !keyLoading && !apiKey ? (
              <p className="text-xs text-gray-400">
                No active API key found for this cluster — shadow accuracy is unavailable.
              </p>
            ) : !shadow ? (
              <p className="text-xs text-gray-400">Loading shadow metrics…</p>
            ) : shadow.note ? (
              <p className="text-xs text-gray-400">{shadow.note}</p>
            ) : (
              <div className="space-y-3">
                {/* Precision / recall by flag threshold */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-500 border-b">
                      <tr>
                        <th className="py-1.5 pr-3 font-medium">Threshold</th>
                        <th className="py-1.5 pr-3 font-medium text-right">Flagged</th>
                        <th className="py-1.5 pr-3 font-medium text-right">Precision</th>
                        <th className="py-1.5 pr-3 font-medium text-right">Recall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(shadow.thresholds || []).map((t) => (
                        <tr
                          key={t.threshold}
                          className={`border-b last:border-0 ${
                            isKeyThreshold(t.threshold)
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 font-medium'
                              : ''
                          }`}
                        >
                          <td className="py-1.5 pr-3 text-xs tabular-nums">{t.threshold}</td>
                          <td className="py-1.5 pr-3 text-right text-xs tabular-nums">
                            {t.flagged ?? '—'}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-xs tabular-nums">
                            {pct(t.precision)}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-xs tabular-nums">
                            {pct(t.recall)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Calibration: predicted vs observed rate per probability band */}
                {(shadow.calibration || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {shadow.calibration.map((c) => (
                      <span
                        key={c.band}
                        title={`n=${c.n}`}
                        className={`text-xs px-2 py-1 rounded border tabular-nums ${calibrationTint(
                          Math.abs((c.predicted || 0) - (c.observed || 0)),
                        )}`}
                      >
                        {c.band}: predicted {pct(c.predicted)} vs observed {pct(c.observed)}
                      </span>
                    ))}
                  </div>
                )}

                {/* Top markets by interruptions */}
                {(shadow.markets || []).length > 0 && (
                  <div className="text-xs text-gray-600">
                    <div className="text-slate-500 uppercase tracking-wide mb-1">Top markets</div>
                    <ul className="space-y-0.5">
                      {[...shadow.markets]
                        .sort(
                          (a, b) =>
                            (b.interrupted || 0) - (a.interrupted || 0) ||
                            (b.resolved || 0) - (a.resolved || 0),
                        )
                        .slice(0, 5)
                        .map((m) => (
                          <li key={`${m.family}-${m.az}`} className="tabular-nums">
                            {m.family} {m.az} — {m.interrupted}/{m.resolved}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default SpotReliabilityPanel;
